import { useCallback, useEffect, useState } from 'react';
import { useTranslate, useNotify, usePermissions } from 'react-admin';
import {
  Card,
  CardContent,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Pagination,
  Stack,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Refresh } from '@mui/icons-material';
import { apiClient } from '../../utils/apiClient';
import { hasPermission } from '../../utils/hasPermission';
import { InvestigationDetailDialog } from './InvestigationDetailDialog';
import { ModelAbSection } from './ModelAbSection';
import { PromoteToPlaybookButton } from '../ai-playbooks';
import {
  StatusChip,
  CategoryChip,
  ConfidenceChip,
  formatTimestamp,
  formatModel,
  anchorTypeLabel,
} from './reportDisplay';
import type { InvestigationListItem } from './types';

interface InvestigationHistoryListProps {
  // Bump to force a re-fetch of page 1 (e.g. after a run completes).
  refreshSignal?: number;
}

const PER_PAGE = 20;

interface ListResponse {
  data: InvestigationListItem[];
  total: number;
  limit: number;
  offset: number;
}

// Paginated history list (GET /investigations, admin {data,total,limit,offset}) with a row-click
// detail drill-in. Uses apiClient directly (self-contained; does not depend on dataProvider
// resource resolution) and a manual Table + MUI Pagination, mirroring OcppLogsQueryPage.
export const InvestigationHistoryList = ({ refreshSignal }: InvestigationHistoryListProps) => {
  const translate = useTranslate();
  const notify = useNotify();
  const { permissions } = usePermissions();
  // Phase-2 (sub-plan 20) Model A/B is reachable only to manage-permission operators (REF-03 s.5).
  const canManage = hasPermission(
    permissions as string[] | null | undefined,
    'ai_troubleshooting:manage:all',
  );

  const [rows, setRows] = useState<InvestigationListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const fetchHistory = useCallback(
    async (targetPage: number) => {
      setLoading(true);
      setError(null);
      try {
        const offset = (targetPage - 1) * PER_PAGE;
        const res = await apiClient.get<ListResponse>(
          `/api/backend/ai-troubleshooting/investigations?limit=${PER_PAGE}&offset=${offset}`,
        );
        setRows(res.data || []);
        setTotal(res.total || 0);
      } catch (err) {
        const message =
          (err as { message?: string })?.message ||
          translate('aiTroubleshooting.history.loadFailed');
        setError(message);
        notify(translate('aiTroubleshooting.history.loadFailed'), { type: 'error' });
      } finally {
        setLoading(false);
      }
    },
    [notify, translate],
  );

  // Initial load + reload when the refresh signal changes (reset to page 1).
  useEffect(() => {
    setPage(1);
    fetchHistory(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshSignal]);

  const handlePageChange = (_e: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
    fetchHistory(value);
  };

  return (
    <Card sx={{ mb: { xs: 2, sm: 3 } }}>
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: { xs: 1.5, sm: 2 },
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {translate('aiTroubleshooting.history.title')}
          </Typography>
          <Tooltip title={translate('aiTroubleshooting.history.refresh')}>
            <span>
              <IconButton onClick={() => fetchHistory(page)} disabled={loading} size="small">
                <Refresh />
              </IconButton>
            </span>
          </Tooltip>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : rows.length === 0 ? (
          <Alert severity="info">{translate('aiTroubleshooting.history.empty')}</Alert>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{translate('aiTroubleshooting.history.createdAt')}</TableCell>
                    <TableCell>{translate('aiTroubleshooting.history.status')}</TableCell>
                    <TableCell>{translate('aiTroubleshooting.history.anchor')}</TableCell>
                    <TableCell>{translate('aiTroubleshooting.history.category')}</TableCell>
                    <TableCell>{translate('aiTroubleshooting.history.confidence')}</TableCell>
                    <TableCell>{translate('aiTroubleshooting.history.model')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow
                      key={row.id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => setSelectedId(row.id)}
                    >
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {formatTimestamp(row.createdAt)}
                      </TableCell>
                      <TableCell>
                        <StatusChip translate={translate} status={row.status} />
                      </TableCell>
                      <TableCell>
                        {anchorTypeLabel(translate, row.anchorType)}
                        {row.anchorId ? ` (${row.anchorId})` : ''}
                      </TableCell>
                      <TableCell>
                        {row.rootCauseCategory ? (
                          <CategoryChip translate={translate} category={row.rootCauseCategory} />
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell>
                        {row.confidence ? (
                          <ConfidenceChip translate={translate} confidence={row.confidence} />
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatModel(row.model)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {total > PER_PAGE && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: { xs: 2, sm: 3 } }}>
                <Stack spacing={2}>
                  <Pagination
                    count={Math.ceil(total / PER_PAGE)}
                    page={page}
                    onChange={handlePageChange}
                    color="primary"
                    showFirstButton
                    showLastButton
                  />
                </Stack>
              </Box>
            )}
          </>
        )}
      </CardContent>

      <InvestigationDetailDialog
        investigationId={selectedId}
        open={selectedId !== null}
        onClose={() => setSelectedId(null)}
        bodyContent={
          // Sub-plan 20: Model A/B replay + comparison, mounted below the Phase-1 report/handoff.
          // Hidden entirely for a use-only operator.
          canManage && selectedId ? <ModelAbSection investigationId={selectedId} /> : null
        }
        headerActions={(() => {
          const selected = rows.find((r) => r.id === selectedId);
          if (!selected) return null;
          // Sub-plan 18 injection: self-gates on status === 'completed' + manage permission.
          return (
            <PromoteToPlaybookButton
              investigation={{
                investigationId: selected.id,
                status: selected.status,
                rootCauseCategory: selected.rootCauseCategory,
              }}
            />
          );
        })()}
      />
    </Card>
  );
};

export default InvestigationHistoryList;
